function collectionAnalyticsIdentifier(value) {
  return (
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '_')
      .replace(/^[_-]+|[_-]+$/g, '')
      .slice(0, 96) || 'filter'
  );
}

function trackCollectionAnalyticsInteraction(target, interactionName, interactionResult) {
  if (!target?.dataset?.analyticsElementId) return;

  const sortControl =
    target.matches?.('.collection-sort__input, select[name="sort_by"]') ||
    target.closest?.('[data-analytics-component-id="collection_sort"]');
  const details = {
    componentId: sortControl ? 'collection_sort' : 'collection_filters',
    elementType: target.dataset.analyticsElementType || (sortControl ? 'sort_option' : 'filter'),
    elementId: sortControl
      ? collectionAnalyticsIdentifier(target.value || target.dataset.analyticsElementId)
      : target.dataset.analyticsElementId,
    interactionName,
  };

  if (interactionResult) details.interactionResult = interactionResult;
  window.TorrasAnalytics?.trackInteraction(target, details);
}

function decorateCollectionAnalytics(root = document) {
  const filterRoot = root.matches?.('#main-collection-filters[data-analytics-component-id="collection_filters"]')
    ? root
    : root.querySelector?.('#main-collection-filters[data-analytics-component-id="collection_filters"]');
  if (!filterRoot) return;

  filterRoot
    .querySelectorAll('input[type="checkbox"], .range-slider__input')
    .forEach((input) => {
      if (input.matches('.collection-sort__input')) return;

      const group = input.closest('.js-filter, price-range') || filterRoot;
      const groupInputs = Array.from(
        group.querySelectorAll(input.matches('.range-slider__input') ? '.range-slider__input' : 'input[type="checkbox"]'),
      );
      const position = Math.max(groupInputs.indexOf(input) + 1, 1);
      const parameter = input.matches('.range-slider__input')
        ? 'price'
        : collectionAnalyticsIdentifier(input.getAttribute('name'));
      const suffix = input.matches('.range-slider__input--lower')
        ? 'min'
        : input.matches('.range-slider__input--upper')
          ? 'max'
          : `option_${position}`;
      const elementId = collectionAnalyticsIdentifier(`${parameter}_${suffix}`);
      const label = input.closest('label') || input;

      [label, input].forEach((element) => {
        element.dataset.analyticsClick = '';
        element.dataset.analyticsElementType = 'filter';
        element.dataset.analyticsElementId = elementId;
        element.dataset.analyticsElementPosition = String(position);
      });
      label.dataset.analyticsImpression = '';
    });

  filterRoot.querySelectorAll('.js-filter:not(.mobile-facets__details--sort) > summary').forEach((summary) => {
    const details = summary.closest('.js-filter');
    const firstInput = details?.querySelector('input[name], .range-slider__input');
    const groupId = collectionAnalyticsIdentifier(
      firstInput?.getAttribute('name') || details?.dataset?.index || 'filter_group',
    );
    summary.dataset.analyticsClick = '';
    summary.dataset.analyticsImpression = '';
    summary.dataset.analyticsElementType = 'filter';
    summary.dataset.analyticsElementId = `group_${groupId}`;
    if (summary.dataset.collectionAnalyticsBound !== 'true') {
      summary.dataset.collectionAnalyticsBound = 'true';
      summary.addEventListener('click', () => {
        trackCollectionAnalyticsInteraction(summary, details?.open ? 'collapse' : 'expand', 'success');
      });
    }
  });

  filterRoot.querySelectorAll('facet-remove a').forEach((link) => {
    const details = link.closest('.js-filter');
    const firstInput = details?.querySelector('input[name], .range-slider__input');
    const groupId = collectionAnalyticsIdentifier(firstInput?.getAttribute('name') || 'all');
    link.dataset.analyticsClick = '';
    link.dataset.analyticsImpression = '';
    link.dataset.analyticsElementType = 'filter';
    link.dataset.analyticsElementId = groupId === 'all' ? 'clear_all' : `clear_${groupId}`;
  });

  filterRoot.querySelectorAll('.mobile-facets__footer .button').forEach((button) => {
    button.dataset.analyticsClick = '';
    button.dataset.analyticsImpression = '';
    button.dataset.analyticsElementType = 'button';
    button.dataset.analyticsElementId = 'apply';
    if (button.dataset.collectionAnalyticsBound !== 'true') {
      button.dataset.collectionAnalyticsBound = 'true';
      button.addEventListener('click', () => {
        trackCollectionAnalyticsInteraction(button, 'apply_filter');
      });
    }
  });

  window.TorrasAnalytics?.registerTree(filterRoot);
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    decorateCollectionAnalytics(document);
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    decorateCollectionAnalytics(document);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__accordion-summary`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const additionalElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
      '.yg-facets-toolbar-bar',
    ];

    additionalElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    decorateCollectionAnalytics(this.closest('#main-collection-filters') || document);
    if (event.target.matches('.collection-sort__input, select[name="sort_by"]')) {
      trackCollectionAnalyticsInteraction(event.target, 'select_sort', 'success');
    } else if (event.target.matches('input[type="checkbox"], .range-slider__input')) {
      trackCollectionAnalyticsInteraction(event.target, 'select_filter');
    }
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.filter(Boolean).join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    trackCollectionAnalyticsInteraction(event.currentTarget, 'clear_filter');
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.hiddenInputs = this.querySelectorAll('.facets__price-input');
    this.rangeInputs = this.querySelectorAll('.range-slider__input');
    this.rangeFill = this.querySelector('.range-slider-used__inner');
    this.valueOutputs = this.querySelectorAll('.facets__price__range-value');

    this.rangeInputs.forEach((element) => {
      element.addEventListener('input', this.onRangeInput.bind(this));
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.setMinAndMaxValues();
    this.updateVisualState();
  }

  onRangeInput(event) {
    this.syncRangeValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateVisualState();
  }

  onRangeChange(event) {
    this.syncRangeValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateVisualState();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.hiddenInputs[0];
    const maxInput = this.hiddenInputs[1];
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));

    if (this.rangeInputs.length === 2) {
      this.rangeInputs[0].max = maxInput.value || maxInput.getAttribute('data-max') || this.rangeInputs[0].max;
      this.rangeInputs[1].min = minInput.value || 0;
    }
  }

  syncRangeValues(changedInput) {
    if (this.rangeInputs.length !== 2 || this.hiddenInputs.length !== 2) return;

    const lowerRange = this.rangeInputs[0];
    const upperRange = this.rangeInputs[1];
    let lowerValue = Number(lowerRange.value);
    let upperValue = Number(upperRange.value);

    if (changedInput === lowerRange && lowerValue > upperValue) {
      upperValue = lowerValue;
      upperRange.value = String(upperValue);
    }

    if (changedInput === upperRange && upperValue < lowerValue) {
      lowerValue = upperValue;
      lowerRange.value = String(lowerValue);
    }

    const rangeMax = Number(this.hiddenInputs[1].getAttribute('data-max') || upperRange.max || 0);

    this.hiddenInputs[0].value = lowerValue > 0 ? String(lowerValue) : '';
    this.hiddenInputs[0].disabled = lowerValue <= 0;
    this.hiddenInputs[1].value = upperValue < rangeMax ? String(upperValue) : '';
    this.hiddenInputs[1].disabled = upperValue >= rangeMax;
  }

  updateVisualState() {
    this.updateOutputLabels();
    this.updateSliderTrack();
  }

  updateOutputLabels() {
    if (this.hiddenInputs.length !== 2 || this.valueOutputs.length !== 2) return;

    const lowerValue = Number(this.rangeInputs[0]?.value || 0);
    const upperValue = Number(this.rangeInputs[1]?.value || 0);
    this.valueOutputs[0].textContent = this.formatMoney(lowerValue, this.valueOutputs[0].textContent);
    this.valueOutputs[1].textContent = this.formatMoney(upperValue, this.valueOutputs[1].textContent);
  }

  updateSliderTrack() {
    if (!this.rangeFill || this.hiddenInputs.length !== 2) return;

    const lowerValue = Number(this.rangeInputs[0]?.value || 0);
    const upperValue = Number(this.rangeInputs[1]?.value || 0);
    const rangeMax = Number(this.hiddenInputs[1].getAttribute('data-max') || this.rangeInputs[1]?.max || 0);

    if (!rangeMax) {
      this.rangeFill.style.left = '0%';
      this.rangeFill.style.width = '100%';
      return;
    }

    const start = (lowerValue / rangeMax) * 100;
    const end = (upperValue / rangeMax) * 100;
    this.rangeFill.style.left = `${start}%`;
    this.rangeFill.style.width = `${Math.max(end - start, 0)}%`;
  }

  formatMoney(value, sampleText) {
    const amount = Number(value || 0);
    const sample = sampleText || '';
    const numberMatch = sample.match(/[-\d.,]+/);
    const currencyPrefix = numberMatch ? sample.slice(0, numberMatch.index) : '';
    const currencySuffix = numberMatch ? sample.slice(numberMatch.index + numberMatch[0].length) : '';
    const formattedAmount = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${currencyPrefix}${formattedAmount}${currencySuffix}`;
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
decorateCollectionAnalytics(document);
